import { useState } from "react";
import { useProcessModular } from "../../pages/ProcessModular/useProcessModular";
import {
  Box,
  Text,
  HStack,
  Button,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Textarea,
  Portal,
} from "@chakra-ui/react";
import { ProcessModelPropertiesModal } from "../ProcessModelPropertiesModal/ProcessModelPropertiesModal";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "@/services/state/store";
import { setIsConnect } from "../../services/state/Process-modular/processModular";
import axios from "axios";
import { Spinner } from "@chakra-ui/react";

interface IToolBarItem {
  id: string;
  name: string;
  component?: JSX.Element;
  onSelect?: () => void;
}

interface ToolBarProps {
  onAdd: () => void;
  onSaveToDB: () => Promise<void>;
  onFileRestore: (event: React.ChangeEvent<HTMLInputElement>) => void;
  checkNodes: () => void;
  isInstanceBased?: boolean;
  onJsonRestore1: (jsonInput: string) => void;
}

export const ToolBar = ({
  onAdd,
  onSaveToDB,
  onFileRestore,
  checkNodes,
  isInstanceBased,
  onJsonRestore1,
}: ToolBarProps) => {
  const {
    onAdd: onProcessModularAdd,
    onSaveToDB: onProcessModularSave,
    onFileRestore: onProcessModularFileRestore,
    checkNodes: onCheckNodes,
  } = useProcessModular({});
  const dispatch: AppDispatch = useDispatch();

  // Existing disclosure for ProcessModelPropertiesModal
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [promptInput, setPromptInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [conversionError, setConversionError] = useState("");
  // New disclosure for the JSON Prompt modal
  const {
    isOpen: isPromptModalOpen,
    onOpen: onPromptModalOpen,
    onClose: onPromptModalClose,
  } = useDisclosure();
  // New state to hold JSON input from the textarea
  const [jsonInput, setJsonInput] = useState("");

  const connect = useSelector(
    (state: RootState) => state.processModeler.isConnect
  );

  const toggleConnect = (value: boolean) => {
    dispatch(setIsConnect(value));
  };

  const handleClose = () => {
    window.close();
  };
  // data for toolbar items
  const toolBarItems: IToolBarItem[] = [
    {
      id: "Connect",
      name: "Connect",
      onSelect: () => toggleConnect(!connect),
    },
  ];

  // Menu items with click handlers instead of components
  const menuItems: IToolBarItem[] = [
    {
      id: "open",
      name: "Open",
      onSelect: () => {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = "application/json";
        input.onchange = (event) => {
          const target = event.target as HTMLInputElement;
          const reactEvent = {
            target,
            currentTarget: target,
            nativeEvent: event,
            isDefaultPrevented: () => false,
            isPropagationStopped: () => false,
            persist: () => {},
          } as React.ChangeEvent<HTMLInputElement>;

          if (onFileRestore) {
            onFileRestore(reactEvent);
          } else if (onProcessModularFileRestore) {
            onProcessModularFileRestore(reactEvent);
          }
        };
        input.click();
      },
    },
    {
      id: "save",
      name: "Save",
      onSelect: onSaveToDB || onProcessModularSave,
    },
    {
      id: "properties",
      name: "Properties",
      onSelect: onOpen,
    },
    {
      id: "prompt",
      name: "Prompt",
      onSelect: onPromptModalOpen,
    },
    {
      id: "exit",
      name: "Exit",
      onSelect: handleClose,
    },
  ];
  const handleConvertPrompt = async () => {
    try {
      // Add your actual prompt-to-JSON conversion logic here
      // This is a mock implementation - replace with real API call or conversion logic
      const convertedJson = await convertPromptToJson(promptInput);

      // Validate JSON
      JSON.parse(convertedJson);

      setJsonInput(convertedJson);
      setConversionError("");
    } catch (error) {
      setConversionError("Failed to convert prompt to valid JSON");
      console.error("Conversion error:", error);
    }
  };
  const onGenerateJsonClick = async (prompt: string) => {
    if (!prompt || prompt.trim().length < 10) {
      setConversionError("Prompt must be at least 10 characters long");
      return;
    }

    setIsLoading(true);
    setConversionError("");

    try {
      const data = { user_input: prompt };

      const response = await axios.post("/generate_workflow", data, {
        baseURL: process.env.REACT_APP_JSON_GENERATOR_URL,
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.status === 200 && response.data.status === "success") {
        const generatedWorkflow = response.data.data;
        setJsonInput(JSON.stringify(generatedWorkflow, null, 2));
        setConversionError("");
      } else {
        setConversionError(
          response.data.message || "Failed to generate JSON from prompt"
        );
      }
    } catch (error: any) {
      console.error("Error generating JSON:", error);
      setConversionError(
        error?.response?.data?.message ||
          "Something went wrong while generating JSON"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const convertPromptToJson = async (prompt: string) => {
    // Example conversion logic - modify based on your requirements
    return JSON.stringify(
      {
        processModelDtls: {
          process_model: {
            pm: {
              meta: {
                displayName: {
                  localeList: [{ value: prompt }],
                },
              },
              nodes: [],
              edges: [],
              pvs: [],
            },
          },
        },
      },
      null,
      2
    );
  };
  return (
    <Box
      mt={2}
      px={2}
      py={1}
      bg="rgba(255, 255, 255, 0.05)"
      backdropFilter="blur(20px)"
      border="1px solid rgba(255, 255, 255, 0.1)"
      // borderRadius="12px"
      borderTopRadius="12px"
      width={"100%"}
      color="white"
      minHeight="40px"
      display="flex"
      alignItems="center"
    >
      <HStack
        width={"100%"}
        mt={{ base: 2, sm: 6, md: 4, lg: 2 }}
        spacing={4}
        alignItems="center"
      >
        <Menu placement="bottom-start" strategy="fixed">
          <MenuButton
            as={Button}
            bg="rgba(255, 255, 255, 0.1)"
            borderColor="rgba(255, 255, 255, 0.2)"
            color="white"
            _hover={{
              bg: "rgba(255, 255, 255, 0.2)",
              borderColor: "brand.400",
            }}
            size="sm"
            height="32px"
          >
            Files
          </MenuButton>
          <Portal>
            <MenuList
              bg="rgba(0, 0, 0, 0.95)"
              backdropFilter="blur(20px)"
              border="1px solid rgba(255, 255, 255, 0.2)"
              borderRadius="8px"
              boxShadow="0 8px 32px rgba(0, 0, 0, 0.3)"
              zIndex={99999}
              minW="200px"
              p={2}
            >
              {menuItems.map((menuItem) => (
                <MenuItem
                  key={menuItem.id}
                  bg="transparent"
                  _hover={{ bg: "rgba(255, 255, 255, 0.1)" }}
                  _focus={{ bg: "rgba(255, 255, 255, 0.1)" }}
                  color="white"
                  borderRadius="4px"
                  mb={1}
                  p={2}
                  onClick={menuItem.onSelect}
                  cursor="pointer"
                >
                  <Text>{menuItem.name}</Text>
                </MenuItem>
              ))}
            </MenuList>
          </Portal>
        </Menu>

        {toolBarItems.map((toolbar) => (
          <Box
            key={toolbar.id}
            display="flex"
            alignItems="center"
            onClick={toolbar.onSelect}
            cursor="pointer"
            _hover={{ color: "brand.400" }}
            color="white"
            px={3}
            py={2}
            borderRadius="md"
            transition="all 0.2s ease"
          >
            <Text>{toolbar.name}</Text>
          </Box>
        ))}
      </HStack>
      <ProcessModelPropertiesModal
        isOpen={isOpen}
        onClose={onClose}
        video=""
        isInstanceBased={isInstanceBased}
      />

      {/* New Modal for JSON input via Prompt */}
      <Modal isOpen={isPromptModalOpen} onClose={onPromptModalClose} isCentered>
        <ModalOverlay />
        <ModalContent
          maxW="600px"
          h="600px"
          bg="rgba(0, 0, 0, 0.9)"
          backdropFilter="blur(20px)"
          border="1px solid rgba(255, 255, 255, 0.4)"
          color="white"
        >
          <ModalHeader color="white">Create Workflow from Prompt</ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody>
            <Box mb={4}>
              <Text mb={2} fontWeight="medium" color="white">
                Describe your workflow:
              </Text>
              <Textarea
                placeholder="Enter your workflow description/prompt..."
                value={promptInput}
                onChange={(e) => setPromptInput(e.target.value)}
                size="sm"
                h="100px"
                mb={2}
                bg="rgba(255, 255, 255, 0.1)"
                borderColor="rgba(255, 255, 255, 0.2)"
                color="white"
                _placeholder={{ color: "rgba(255, 255, 255, 0.6)" }}
                _focus={{
                  borderColor: "brand.500",
                  boxShadow: "0 0 0 1px rgba(118, 82, 247, 0.5)",
                }}
              />
              <Button
                colorScheme="purple"
                size="sm"
                onClick={() => onGenerateJsonClick(promptInput)}
                isDisabled={!promptInput || isLoading}
                leftIcon={isLoading ? <Spinner size="xs" /> : undefined}
              >
                {isLoading ? "Generating..." : "Generate JSON"}
              </Button>
            </Box>

            <Box>
              <Text mb={2} fontWeight="medium" color="white">
                Generated JSON:
              </Text>
              {conversionError && (
                <Text color="red.400" fontSize="sm" mt={1}>
                  {conversionError}
                </Text>
              )}

              <Textarea
                placeholder="JSON will appear here..."
                value={jsonInput}
                onChange={(e) => setJsonInput(e.target.value)}
                size="sm"
                h="200px"
                fontFamily="monospace"
                bg="rgba(255, 255, 255, 0.1)"
                borderColor="rgba(255, 255, 255, 0.2)"
                color="white"
                _placeholder={{ color: "rgba(255, 255, 255, 0.6)" }}
                _focus={{
                  borderColor: "brand.500",
                  boxShadow: "0 0 0 1px rgba(118, 82, 247, 0.5)",
                }}
              />
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="purple"
              mr={3}
              onClick={() => {
                onPromptModalClose();
                onJsonRestore1(jsonInput);
              }}
              isDisabled={!jsonInput}
            >
              Add Workflow
            </Button>
            <Button
              variant="ghost"
              onClick={onPromptModalClose}
              color="white"
              _hover={{ bg: "rgba(255, 255, 255, 0.1)" }}
            >
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};
